import React, { useState, useEffect } from 'react';
import { coursesService } from '../../services/firestoreService';
import { interactiveLearningService } from '../../services/interactiveLearningService';
import TopicInteractiveLearning from './TopicInteractiveLearning';
import './CourseVisualLearning.css';

const CourseVisualLearning = ({ courseId, sessionId, userId, userName }) => {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTopicIndex, setActiveTopicIndex] = useState(0);
  const [completedTopics, setCompletedTopics] = useState(new Set());
  const [topicContents, setTopicContents] = useState({});
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const loadCourse = async () => {
      try {
        const courseData = await coursesService.getCourse(courseId);
        setCourse(courseData);
        
        // Load interactive learning content for all topics
        if (courseData?.topics) {
          const contents = {};
          for (const topic of courseData.topics) {
            try {
              const content = await interactiveLearningService.getTopicContent(courseId, topic.name);
              if (content) {
                contents[topic.name] = content;
              }
            } catch (error) {
              // No content for this topic or permission issue, continue
              console.debug('No content found for topic:', topic.name);
            }
          }
          setTopicContents(contents);
        }
      } catch (error) {
        console.error('Error loading course:', error);
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  useEffect(() => {
    // Calculate progress
    if (course?.topics) {
      const total = course.topics.length;
      const completed = completedTopics.size;
      setProgress(Math.round((completed / total) * 100));
    }
  }, [completedTopics, course]);

  const handleTopicComplete = (topicName) => {
    setCompletedTopics(prev => new Set([...prev, topicName]));
    // Auto-advance to next topic if available
    if (activeTopicIndex < course.topics.length - 1) {
      setTimeout(() => {
        setActiveTopicIndex(activeTopicIndex + 1);
      }, 1500);
    }
  };

  const handleTopicClick = (index) => {
    // Allow clicking on completed topics or the next available topic
    if (completedTopics.has(course.topics[index].name) || index === activeTopicIndex || index === activeTopicIndex + 1) {
      setActiveTopicIndex(index);
    }
  };

  if (loading) {
    return (
      <div className="course-visual-learning">
        <div className="loading-state">
          <div className="loading-spinner">🎓</div>
          <p>Loading course content...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="course-visual-learning">
        <div className="error-state">
          <p>Course not found</p>
        </div>
      </div>
    );
  }

  const currentTopic = course.topics[activeTopicIndex];
  const isTopicCompleted = completedTopics.has(currentTopic?.name);
  const canAccessTopic = (index) => {
    if (index === 0) return true; // First topic always accessible
    if (completedTopics.has(course.topics[index - 1].name)) return true; // Previous topic completed
    return index <= activeTopicIndex; // Current or already accessed
  };

  return (
    <div className="course-visual-learning">
      {/* Course Header */}
      <div className="course-header-visual">
        <div className="course-title-section">
          <h1>📚 {course.title}</h1>
          {course.description && (
            <p className="course-description-visual">{course.description}</p>
          )}
        </div>
        <div className="progress-section">
          <div className="progress-circle">
            <svg className="progress-ring" width="120" height="120">
              <circle
                className="progress-ring-background"
                cx="60"
                cy="60"
                r="54"
                fill="none"
                stroke="#e0e0e0"
                strokeWidth="8"
              />
              <circle
                className="progress-ring-fill"
                cx="60"
                cy="60"
                r="54"
                fill="none"
                stroke="#667eea"
                strokeWidth="8"
                strokeDasharray={`${2 * Math.PI * 54}`}
                strokeDashoffset={`${2 * Math.PI * 54 * (1 - progress / 100)}`}
                transform="rotate(-90 60 60)"
              />
            </svg>
            <div className="progress-text">
              <span className="progress-percentage">{progress}%</span>
              <span className="progress-label">Complete</span>
            </div>
          </div>
          <div className="progress-stats">
            <div className="stat-item">
              <span className="stat-value">{completedTopics.size}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{course.topics.length - completedTopics.size}</span>
              <span className="stat-label">Remaining</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{course.topics.length}</span>
              <span className="stat-label">Total Topics</span>
            </div>
          </div>
        </div>
      </div>

      {/* Progressive Topic Path */}
      <div className="topic-path-container">
        <h2>🎯 Learning Path</h2>
        <div className="topic-path">
          {course.topics.map((topic, index) => {
            const isCompleted = completedTopics.has(topic.name);
            const isActive = index === activeTopicIndex;
            const isAccessible = canAccessTopic(index);
            const hasContent = topic.sampleCode || topicContents[topic.name];

            return (
              <div
                key={index}
                className={`topic-node ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${!isAccessible ? 'locked' : ''}`}
                onClick={() => handleTopicClick(index)}
              >
                <div className="node-connector">
                  {index > 0 && (
                    <div className={`connector-line ${completedTopics.has(course.topics[index - 1].name) ? 'completed' : ''}`} />
                  )}
                </div>
                <div className="node-content">
                  <div className="node-icon">
                    {isCompleted ? '✅' : isActive ? '🎯' : !isAccessible ? '🔒' : hasContent ? '📖' : '📝'}
                  </div>
                  <div className="node-info">
                    <h4>{topic.name}</h4>
                    {topic.hours && (
                      <span className="node-hours">{topic.hours}h</span>
                    )}
                  </div>
                  {isCompleted && (
                    <div className="completion-badge">✓</div>
                  )}
                </div>
                {index < course.topics.length - 1 && (
                  <div className={`connector-line-right ${isCompleted ? 'completed' : ''}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Current Topic Content */}
      {currentTopic && (
        <div className="current-topic-section">
          <div className="topic-header-visual">
            <div className="topic-title-bar">
              <h2>
                <span className="topic-number">Topic {activeTopicIndex + 1} of {course.topics.length}</span>
                <span className="topic-name-visual">{currentTopic.name}</span>
              </h2>
              {isTopicCompleted && (
                <div className="topic-completed-badge">
                  <span>✅ Completed</span>
                </div>
              )}
            </div>
            {currentTopic.description && (
              <p className="topic-description-visual">{currentTopic.description}</p>
            )}
            {currentTopic.hours && (
              <div className="topic-meta">
                <span className="meta-item">⏱️ {currentTopic.hours} hours</span>
              </div>
            )}
          </div>

          {/* Sample Code Display */}
          {currentTopic.sampleCode && (
            <div className="sample-code-visual-section">
              <h3>💻 Sample Code</h3>
              <div className="code-display-visual">
                <div className="code-header-visual">
                  <span>Python</span>
                  <button
                    className="copy-code-btn-visual"
                    onClick={() => {
                      navigator.clipboard.writeText(currentTopic.sampleCode);
                      alert('Code copied to clipboard!');
                    }}
                  >
                    📋 Copy
                  </button>
                </div>
                <pre className="code-block-visual">
                  <code>{currentTopic.sampleCode}</code>
                </pre>
              </div>
            </div>
          )}

          {/* Interactive Learning */}
          <div className="interactive-learning-visual-section">
            {!topicContents[currentTopic.name] && (
              <p className="interactive-learning-note">
                Interactive activities are being generated for this topic. You can still use the quick activities below while the custom content loads.
              </p>
            )}
            <TopicInteractiveLearning
              userId={userId}
              userName={userName}
              topic={currentTopic}
              courseId={courseId}
              onProgressUpdate={(stars) => {
                if (stars > 0) {
                  handleTopicComplete(currentTopic.name);
                }
              }}
            />
          </div>

          {/* Navigation Buttons */}
          <div className="topic-navigation">
            <button
              className="nav-btn prev-btn"
              onClick={() => setActiveTopicIndex(Math.max(0, activeTopicIndex - 1))}
              disabled={activeTopicIndex === 0}
            >
              ← Previous Topic
            </button>
            <button
              className="nav-btn next-btn"
              onClick={() => {
                if (activeTopicIndex < course.topics.length - 1) {
                  setActiveTopicIndex(activeTopicIndex + 1);
                }
              }}
              disabled={activeTopicIndex >= course.topics.length - 1}
            >
              Next Topic →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseVisualLearning;

